import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import styles from "./Form.module.css";

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: (id: string, describedBy: string | undefined) => ReactNode;
  className?: string;
}

/** Label + control + hint/error, wired with ids so screen readers read them together. */
export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className={[styles.field, className].filter(Boolean).join(" ")}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      {children(id, hint || error ? hintId : undefined)}
      {(error || hint) && (
        <p id={hintId} className={error ? styles.error : styles.hint} role={error ? "alert" : undefined}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={[styles.control, className].filter(Boolean).join(" ")} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={[styles.control, styles.textarea, className].filter(Boolean).join(" ")} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return (
    <select ref={ref} className={[styles.control, styles.select, className].filter(Boolean).join(" ")} {...rest}>
      {children}
    </select>
  );
});

export function Switch({ checked, onChange, label, description }: { checked: boolean; onChange: (value: boolean) => void; label: ReactNode; description?: ReactNode }) {
  const id = useId();
  return (
    <div className={styles.switchRow}>
      <div>
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
        {description && <p className={styles.hint}>{description}</p>}
      </div>
      <button id={id} type="button" role="switch" aria-checked={checked} className={styles.switch} onClick={() => onChange(!checked)}>
        <span />
      </button>
    </div>
  );
}

/** Comma/Enter separated chips — for tags, suggestions, forbidden topics, origins. */
export function ChipsInput({ id, value, onChange, placeholder, max = 20 }: { id?: string; value: string[]; onChange: (value: string[]) => void; placeholder?: string; max?: number }) {
  const add = (raw: string) => {
    const items = raw
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
    if (items.length) onChange([...new Set([...value, ...items])].slice(0, max));
  };
  return (
    <div className={`${styles.control} ${styles.chips}`}>
      {value.map((item) => (
        <span key={item} className={styles.chip}>
          {item}
          <button type="button" aria-label={`Hapus ${item}`} onClick={() => onChange(value.filter((other) => other !== item))}>
            ×
          </button>
        </span>
      ))}
      <input
        id={id}
        className={styles.chipInput}
        placeholder={value.length ? "" : placeholder}
        onKeyDown={(event) => {
          const input = event.currentTarget;
          if ((event.key === "Enter" || event.key === ",") && input.value.trim()) {
            event.preventDefault();
            add(input.value);
            input.value = "";
          } else if (event.key === "Backspace" && !input.value && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={(event) => {
          if (event.currentTarget.value.trim()) {
            add(event.currentTarget.value);
            event.currentTarget.value = "";
          }
        }}
      />
    </div>
  );
}

export function FormGrid({ children, columns = 2 }: { children: ReactNode; columns?: 1 | 2 | 3 }) {
  return <div className={`${styles.grid} ${styles[`cols${columns}`]}`}>{children}</div>;
}
